import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ALL_PERMISSION_KEYS } from "../constants/bouncerPermissions";

const PERMISSIONS_STORAGE_KEY = "BOUNCER_PERMISSIONS";
const PERMISSIONS_KEY_SET_HASH_STORAGE_KEY = "BOUNCER_PERMISSIONS_KEY_SET_HASH";

// Cache-invalidation fingerprint. Not a security hash — just a stable
// change-detection token: sort the permission-key values, join with a
// separator that can't appear inside a key. If a Bouncer key rename ships,
// this hash changes, and any pre-rename session's cache is invalidated on
// next Dashboard/TarsAuthGate mount (see isPermissionsCacheStale).
function computeKeySetHash(keys) {
  return [...keys].sort().join("|");
}

const PermissionsContext = createContext(undefined);

/**
 * Provides Bouncer permissions to the component tree.
 *
 * @param {Object} props
 * @param {string[]} props.permissions — array of allowed permission key strings
 * @param {React.ReactNode} props.children
 */
export function PermissionsProvider({ permissions = [], children }) {
  const value = useMemo(() => {
    const permissionSet = new Set(permissions);
    return {
      permissions,
      hasPermission: (key) => permissionSet.has(key),
    };
  }, [permissions]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

/**
 * Hook to access Bouncer permissions.
 * @returns {{ permissions: string[], hasPermission: (key: string) => boolean }}
 */
export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
}

/**
 * Save permissions to sessionStorage (for Sidebar / cross-component access).
 * Also persists the current ALL_PERMISSION_KEYS fingerprint so a later bundle
 * whose key set has changed can invalidate this cache via
 * isPermissionsCacheStale.
 */
export function savePermissionsToStorage(permissions) {
  sessionStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions));
  sessionStorage.setItem(
    PERMISSIONS_KEY_SET_HASH_STORAGE_KEY,
    computeKeySetHash(ALL_PERMISSION_KEYS),
  );
}

/** Load permissions from sessionStorage. */
export function loadPermissionsFromStorage() {
  try {
    return JSON.parse(sessionStorage.getItem(PERMISSIONS_STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

/**
 * True when a cached permission set exists but was fetched against a different
 * ALL_PERMISSION_KEYS shape than the current bundle expects — e.g. after a
 * Bouncer key rename (`VENDOR_ONBOARD` → `FULFILMENT_ACCOUNTS_PAYABLE_VENDOR_CREATE`)
 * ships while a user session is still open. Returns false when no cache exists
 * yet (fresh login path) or when the hash matches.
 */
export function isPermissionsCacheStale() {
  const storedPerms = sessionStorage.getItem(PERMISSIONS_STORAGE_KEY);
  const storedHash = sessionStorage.getItem(PERMISSIONS_KEY_SET_HASH_STORAGE_KEY);
  // Truly fresh / never-authed session — nothing to invalidate.
  if (storedPerms === null) return false;
  // Pre-upgrade cache: permissions present but no hash was ever written
  // (hash-writing landed with this mechanism). Treat as stale so the very
  // first config-driven rename this PR ships is protected too.
  if (storedHash === null) return true;
  return storedHash !== computeKeySetHash(ALL_PERMISSION_KEYS);
}

/**
 * Clears every sessionStorage entry that depends on the Bouncer permission
 * shape. The paired mechanism that repopulates the cache is
 * `useStalePermissionsCacheGuard` — it clears synchronously here, then
 * `window.location.assign("/?return_to=…")` navigates through `Home` →
 * `Callback`, and `Callback.js:68` is the single re-fetch site
 * (`fetchBouncerPermissions()` → `savePermissionsToStorage()` writes the fresh
 * hash). Empty cache is deliberately not fail-open on any route — the caller
 * must trigger that navigation, not just clear.
 */
export function clearPermissionsCache() {
  sessionStorage.removeItem(PERMISSIONS_STORAGE_KEY);
  sessionStorage.removeItem(PERMISSIONS_KEY_SET_HASH_STORAGE_KEY);
  sessionStorage.removeItem("TARS_MODULES_ACCESS");
  sessionStorage.removeItem("TARS_IS_INTERNAL");
}

/**
 * Shared mount-site guard for stale Bouncer-permission cache.
 *
 * Detects staleness synchronously (before any downstream hook reads the
 * allowed-list), clears the cache to prevent a brief PermissionsProvider
 * commit with stale/empty perms, and redirects through login preserving the
 * current URL via ?return_to=. Returns true while the redirect is in flight
 * so callers can suppress their normal render.
 *
 * Extracted so Dashboard and TarsAuthGate can't drift out of sync on this
 * security-relevant guard.
 */
export function useStalePermissionsCacheGuard() {
  const [stale] = useState(() => {
    if (isPermissionsCacheStale()) {
      clearPermissionsCache();
      return true;
    }
    return false;
  });
  useEffect(() => {
    if (stale) {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      window.location.assign(`/?return_to=${encodeURIComponent(returnTo)}`);
    }
  }, [stale]);
  return stale;
}
